import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import ForgotPassword from './components/auth/ForgotPassword';
import Dashboard from './components/Dashboard';
import Portfolio from './components/portfolio/Portfolio';
import LastTransactionComparison from './components/portfolio/LastTransactionComparison';
import Strategies from './components/strategies/Strategies';
import StrategyResults from './components/strategies/StrategyResults';
import PriceTriggers from './components/triggers/PriceTriggers';
import Analysis from './components/analysis/Analysis';
import Charts from './components/Charts';
import VCPScanner from './components/vcp/VCPScanner';
import Admin from './components/admin/Admin';
import Layout from './components/layout/Layout';
import PWAInstallPrompt from './components/pwa/PWAInstallPrompt';
import './App.css';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div>Loading...</div>
      </div>
    );
  }
  
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

// Admin-only route - requires admin privileges
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  
  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div>Loading...</div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  if (!user?.is_admin) {
    return <Navigate to="/" />;
  }
  
  return <>{children}</>;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <div className="App">
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/" element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/portfolio" element={
                <ProtectedRoute>
                  <Layout>
                    <Portfolio />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/trade-review" element={
                <ProtectedRoute>
                  <Layout>
                    <LastTransactionComparison />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/strategies" element={
                <ProtectedRoute>
                  <Layout>
                    <Strategies />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/strategy-results/:executionId" element={
                <ProtectedRoute>
                  <Layout>
                    <StrategyResults />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/price-triggers" element={
                <ProtectedRoute>
                  <Layout>
                    <PriceTriggers />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/analysis" element={
                <ProtectedRoute>
                  <Layout>
                    <Analysis />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/charts" element={
                <ProtectedRoute>
                  <Layout>
                    <Charts />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/vcp" element={
                <ProtectedRoute>
                  <Layout>
                    <VCPScanner />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/admin" element={
                <AdminRoute>
                  <Layout>
                    <Admin />
                  </Layout>
                </AdminRoute>
              } />
            </Routes>
            <PWAInstallPrompt />
            <ToastContainer
              position="top-right"
              autoClose={1500}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
            />
          </div>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
